import { useEffect, useMemo, useState } from 'react';
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  CheckCircle2,
  Database,
  Download,
  Loader2,
  X,
} from 'lucide-react';
import { useModuleStore } from '../../module-store';
import type { ModuleId } from '../../module-store';
import {
  DATA_HUB_MODULE_CONFIGS,
  DATA_HUB_MODULE_LABELS,
  type DataHubOperationDefinition,
  type DataHubSectionDefinition,
  type DataHubSectionId,
} from './data-hub-config';
import './data-hub-modal.css';

interface DataHubModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId?: string | null;
  projectName?: string | null;
}

interface DataHubFeedbackState {
  type: 'success' | 'error';
  message: string;
}

function sanitizeFilePart(value: string | null | undefined): string {
  if (!value) {
    return 'proyecto';
  }

  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'proyecto';
}

function downloadJsonFile(filename: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/geo+json;charset=utf-8',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function getModuleLabel(activeModule: ModuleId | null): string {
  if (!activeModule) {
    return 'Sin módulo activo';
  }

  return DATA_HUB_MODULE_LABELS[activeModule] ?? activeModule;
}

function getSectionLabel(section: DataHubSectionId): string {
  return section === 'import' ? 'Importar' : 'Exportar';
}

function getDistinctFormats(section: DataHubSectionDefinition | null): string[] {
  if (!section) {
    return [];
  }

  return Array.from(
    new Set(section.operations.flatMap((operation) => operation.formats)),
  );
}

function getConnectedOperationCount(section: DataHubSectionDefinition | null): number {
  if (!section) {
    return 0;
  }

  return section.operations.filter((operation) => operation.availability === 'available').length;
}

function groupOperations(section: DataHubSectionDefinition | null): Array<{
  label: string | null;
  operations: DataHubOperationDefinition[];
}> {
  if (!section) {
    return [];
  }

  const groups = new Map<string, DataHubOperationDefinition[]>();
  const orderedLabels: string[] = [];

  section.operations.forEach((operation) => {
    const label = operation.groupLabel ?? '';
    if (!groups.has(label)) {
      groups.set(label, []);
      orderedLabels.push(label);
    }
    groups.get(label)?.push(operation);
  });

  return orderedLabels.map((label) => ({
    label: label || null,
    operations: groups.get(label) ?? [],
  }));
}

function getOperationStatusLabel(operation: DataHubOperationDefinition): string {
  if (operation.availabilityLabel) {
    return operation.availabilityLabel;
  }

  return operation.availability === 'available' ? 'Disponible ahora' : 'Próximamente';
}

function getOperationButtonLabel(operation: DataHubOperationDefinition, isLoading: boolean): string {
  if (isLoading) {
    return operation.action?.kind === 'download-geojson' ? 'Preparando...' : 'Abriendo...';
  }

  if (operation.action) {
    return operation.action.label;
  }

  return 'En preparación';
}

function getCapabilityBadgeLabel(
  section: DataHubSectionId,
  operation: DataHubOperationDefinition,
): string {
  if (section === 'import') {
    return operation.availability === 'available'
      ? 'Importación disponible'
      : 'Importación en preparación';
  }

  return operation.availability === 'available'
    ? 'Exportación disponible'
    : 'Exportación en preparación';
}

function getActionSummaryLabel(
  section: DataHubSectionId,
  operation: DataHubOperationDefinition,
): string {
  if (operation.action?.label) {
    return operation.action.label;
  }

  return section === 'import'
    ? 'Flujo en preparación'
    : 'Descarga en preparación';
}

function getActionSummaryHelper(
  section: DataHubSectionId,
  operation: DataHubOperationDefinition,
): string {
  if (operation.action?.helperText) {
    return operation.action.helperText;
  }

  return section === 'import'
    ? 'La entrada de producto ya es visible, pero el flujo todavía no está conectado.'
    : 'La capacidad ya está declarada, pero la descarga todavía no está conectada.';
}

function getGenericModuleMessage(moduleLabel: string, section: DataHubSectionId): string {
  const sectionLabel = section === 'import' ? 'importación' : 'exportación';
  return `${moduleLabel} todavía no publica operaciones de ${sectionLabel} desde el hub. Cuando se conecte, aparecerán aquí por capacidad, geometría, formato y acción.`;
}

function getActionErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return 'No se pudo completar la acción solicitada.';
}

export function DataHubModal({
  isOpen,
  onClose,
  projectId,
  projectName,
}: DataHubModalProps): JSX.Element | null {
  const activeModule = useModuleStore((state) => state.activeModule);
  const [activeSection, setActiveSection] = useState<DataHubSectionId>('import');
  const [actionInFlightId, setActionInFlightId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<DataHubFeedbackState | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setActiveSection('import');
      setActionInFlightId(null);
      setFeedback(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setActionInFlightId(null);
    setFeedback(null);
  }, [activeModule, activeSection, isOpen]);

  const moduleLabel = useMemo(() => getModuleLabel(activeModule), [activeModule]);
  const moduleConfig = activeModule ? DATA_HUB_MODULE_CONFIGS[activeModule] ?? null : null;
  const activeSectionConfig = moduleConfig?.sections[activeSection] ?? null;
  const hasProjectContext = Boolean(projectId);
  const distinctFormats = useMemo(
    () => getDistinctFormats(activeSectionConfig),
    [activeSectionConfig],
  );
  const groupedOperations = useMemo(
    () => groupOperations(activeSectionConfig),
    [activeSectionConfig],
  );
  const connectedOperationCount = useMemo(
    () => getConnectedOperationCount(activeSectionConfig),
    [activeSectionConfig],
  );

  const handleRunOperation = async (operation: DataHubOperationDefinition): Promise<void> => {
    if (!operation.action) {
      return;
    }

    setActionInFlightId(operation.id);
    setFeedback(null);

    try {
      if (operation.action.kind === 'open-waste-import') {
        window.dispatchEvent(
          new CustomEvent('waste-open-import-wizard', {
            detail: {
              entityType: operation.action.entityType,
            },
          }),
        );
        onClose();
        return;
      }

      if (operation.action.kind === 'open-building-import') {
        window.dispatchEvent(new CustomEvent('building-open-import-wizard'));
        onClose();
        return;
      }

      if (operation.action.kind === 'download-geojson') {
        if (!projectId || !operation.action.fetchData || !operation.action.filePrefix) {
          throw new Error('Falta contexto de proyecto para exportar.');
        }

        const collection = await operation.action.fetchData(projectId);
        const filename = `${sanitizeFilePart(projectName)}-${sanitizeFilePart(moduleLabel)}-${operation.action.filePrefix}.geojson`;
        downloadJsonFile(filename, collection);
        setFeedback({
          type: 'success',
          message: `Descarga iniciada: ${operation.entityLabel} en GeoJSON.`,
        });
      }
    } catch (error) {
      setFeedback({
        type: 'error',
        message: getActionErrorMessage(error),
      });
    } finally {
      setActionInFlightId(null);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="data-hub-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="data-hub-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="data-hub-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="data-hub-header">
          <div className="data-hub-title-block">
            <span className="data-hub-kicker">Intercambio de datos</span>
            <div className="data-hub-title-row">
              <Database size={18} />
              <h2 id="data-hub-title">Datos</h2>
            </div>
            <p className="data-hub-subtitle">
              Centro contextual de importación y exportación para el proyecto y módulo activos.
            </p>
          </div>

          <div className="data-hub-context">
            {projectName ? (
              <span className="data-hub-context-pill" title={projectName}>
                Proyecto: {projectName}
              </span>
            ) : null}
            <span className="data-hub-context-pill">
              Módulo: {moduleLabel}
            </span>
            <span className="data-hub-context-pill">
              Estado: {moduleConfig?.statusLabel ?? 'Sin conexiones publicadas'}
            </span>
          </div>

          <button
            type="button"
            className="data-hub-close-btn"
            onClick={onClose}
            aria-label="Cerrar espacio de datos"
          >
            <X size={18} />
          </button>
        </header>

        <div className="data-hub-section-tabs" role="tablist" aria-label="Secciones de datos">
          <button
            type="button"
            className={`data-hub-section-tab ${activeSection === 'import' ? 'active' : ''}`}
            onClick={() => setActiveSection('import')}
            role="tab"
            aria-selected={activeSection === 'import'}
          >
            <ArrowUpFromLine size={16} />
            <span>Importar</span>
          </button>
          <button
            type="button"
            className={`data-hub-section-tab ${activeSection === 'export' ? 'active' : ''}`}
            onClick={() => setActiveSection('export')}
            role="tab"
            aria-selected={activeSection === 'export'}
          >
            <ArrowDownToLine size={16} />
            <span>Exportar</span>
          </button>
        </div>

        <div className="data-hub-body">
          {!hasProjectContext ? (
            <div className="data-hub-empty-state">
              <h3>Sin proyecto activo</h3>
              <p>Seleccioná un proyecto para habilitar importación y exportación.</p>
            </div>
          ) : !activeModule ? (
            <div className="data-hub-empty-state">
              <h3>Elegí un módulo</h3>
              <p>Este espacio responde al módulo activo. Seleccioná uno para continuar.</p>
            </div>
          ) : !moduleConfig || !activeSectionConfig ? (
            <div className="data-hub-empty-state">
              <h3>{getSectionLabel(activeSection)} en {moduleLabel}</h3>
              <p>{getGenericModuleMessage(moduleLabel, activeSection)}</p>
            </div>
          ) : (
            <div className="data-hub-section-shell">
              <div className="data-hub-section-summary">
                <div className="data-hub-section-copy">
                  <h3>{activeSectionConfig.title}</h3>
                  <p>{activeSectionConfig.description}</p>
                </div>

                <div className="data-hub-section-metrics" aria-label="Resumen de operaciones disponibles">
                  <div className="data-hub-metric-card">
                    <strong>{connectedOperationCount}</strong>
                    <span>acciones conectadas</span>
                  </div>
                  <div className="data-hub-metric-card">
                    <strong>{activeSectionConfig.operations.length}</strong>
                    <span>fichas visibles</span>
                  </div>
                  <div className="data-hub-metric-card">
                    <strong>{distinctFormats.length}</strong>
                    <span>formatos listados</span>
                  </div>
                </div>
              </div>

              <div className="data-hub-section-pills">
                {distinctFormats.map((format) => (
                  <span key={format} className="data-hub-format-pill is-summary">
                    {format}
                  </span>
                ))}
              </div>

              {activeSectionConfig.coverageNote ? (
                <p className="data-hub-section-note">{activeSectionConfig.coverageNote}</p>
              ) : null}

              {moduleConfig?.scopeItems?.length ? (
                <section className="data-hub-module-scope" aria-label="Alcance del módulo">
                  <div className="data-hub-module-scope__header">
                    <h4>{moduleConfig.scopeTitle ?? 'Alcance del módulo'}</h4>
                    <span className="data-hub-module-scope__pill">{moduleConfig.label}</span>
                  </div>
                  <ul className="data-hub-module-scope__list">
                    {moduleConfig.scopeItems.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </section>
              ) : null}

              {groupedOperations.map((group) => (
                <section
                  key={group.label ?? '__default'}
                  className="data-hub-operation-group"
                  aria-label={group.label ?? 'Capacidades publicadas'}
                >
                  {group.label ? <h4 className="data-hub-operation-group__title">{group.label}</h4> : null}

                  <div className="data-hub-operation-list">
                    {group.operations.map((operation) => {
                      const isLoading = actionInFlightId === operation.id;
                      const isAvailable = operation.availability === 'available' && Boolean(operation.action);

                      return (
                        <article
                          key={operation.id}
                          className={`data-hub-operation-card ${!isAvailable ? 'is-disabled' : ''}`}
                        >
                          <div className="data-hub-operation-top">
                            <div className="data-hub-operation-copy">
                              <div className="data-hub-operation-badges">
                                <span className="data-hub-capability-pill">
                                  {getCapabilityBadgeLabel(activeSection, operation)}
                                </span>
                              </div>
                              <h4>{operation.entityLabel}</h4>
                              <p>{operation.description}</p>
                            </div>
                            <span
                              className={`data-hub-status-pill is-${operation.availability}`}
                            >
                              {getOperationStatusLabel(operation)}
                            </span>
                          </div>

                          <div className="data-hub-operation-meta">
                            <div className="data-hub-meta-block">
                              <span className="data-hub-meta-label">Geometría</span>
                              <div className="data-hub-format-list">
                                {operation.geometry.map((geometry) => (
                                  <span key={geometry} className="data-hub-format-pill">
                                    {geometry}
                                  </span>
                                ))}
                              </div>
                            </div>

                            <div className="data-hub-meta-block">
                              <span className="data-hub-meta-label">Formatos</span>
                              <div className="data-hub-format-list">
                                {operation.formats.map((format) => (
                                  <span key={format} className="data-hub-format-pill">
                                    {format}
                                  </span>
                                ))}
                              </div>
                            </div>

                            <div className="data-hub-meta-block">
                              <span className="data-hub-meta-label">Acción disponible</span>
                              <strong className="data-hub-meta-value">
                                {getActionSummaryLabel(activeSection, operation)}
                              </strong>
                              <p>{getActionSummaryHelper(activeSection, operation)}</p>
                            </div>
                          </div>

                          {operation.note ? (
                            <p className="data-hub-operation-note">{operation.note}</p>
                          ) : null}

                          <div className="data-hub-operation-footer">
                            <span className="data-hub-operation-scope">
                              Proyecto activo
                            </span>

                            <button
                              type="button"
                              className={isAvailable ? 'data-hub-secondary-btn' : 'data-hub-disabled-btn'}
                              onClick={() => void handleRunOperation(operation)}
                              disabled={!isAvailable || isLoading}
                            >
                              {isLoading ? (
                                <Loader2 size={16} className="data-hub-spinner" />
                              ) : operation.action?.kind === 'download-geojson' ? (
                                <Download size={16} />
                              ) : (
                                <ArrowUpFromLine size={16} />
                              )}
                              <span>{getOperationButtonLabel(operation, isLoading)}</span>
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </section>
              ))}

              {feedback ? (
                <div
                  className={`data-hub-feedback data-hub-feedback--${feedback.type}`}
                  role="status"
                >
                  {feedback.type === 'success' ? <CheckCircle2 size={16} /> : null}
                  <span>{feedback.message}</span>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
